import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import CadastrarAlunosDetalhe from "./CadastrarAlunosDetalhe";
import "../features/users/user-table.css";

const ContentArea = styled.div`
  padding: 0px 0 20px;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnitTableWrapper = styled.div`
  border-right: 1px solid #dfe3e8;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 18px;
`;

const UnitTitle = styled.div`
  padding: 14px 32px;
  background: #f2f0f0;
  font-size: 16px;
  color: #6ab8a8;
  font-weight: 600;
`;

const UnitTitleValue = styled.span`
  color: #555555;
  padding-left: 8px;
`;

const UnitSeparator = styled.div`
  height: 1px;
  background: #dfe3e8;
`;

const StackedHeaderCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.15;
  font-size: 14px;
  text-align: center;
`;

const GRID_COLUMNS = "1fr 1fr 0.7fr 1fr 1.2fr 0.85fr 1.3fr";

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding-left: 20px;
  height: 50px;
  background: #f2f0f0;
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const UnitRowsWrapper = styled.div``;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding-left: 20px;
  height: 58px;
  font-size: 16px;
  color: #555555;
  font-weight: 500;
  border-top: 1px solid #e6e6e6;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;

    &:last-child {
      border-right: none;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #555555;
`;

const PersonIcon = styled.span`
  font-family: "Material Symbols Outlined", sans-serif;
  font-size: 18px;
  color: #6ab8a8;
`;

const EmptyMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: #555;
  font-size: 15px;
`;

const formatTipoAtividade = (tipo) => {
  const tipos = {
    ESTAGIO_OBRIGATORIO: "Estágio Obrigatório",
    ESTAGIO_SUPERVISIONADO: "Estágio Supervisionado",
    INTERNATO_MEDICO: "Internato Médico",
    AULA_PRATICA: "Aula Prática",
  };
  return tipos[tipo] || tipo || "-";
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pt-BR");
};

const formatPeriodo = (periodos = []) => {
  if (!periodos || periodos.length === 0) return "-";
  const normalized = [...periodos].sort(
    (a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime()
  );
  return normalized
    .map((periodo) => {
      const inicio = formatDate(periodo.dataInicio);
      const fim = formatDate(periodo.dataFim);
      if (!inicio && !fim) return "";
      if (!inicio) return fim;
      if (!fim) return inicio;
      return `${inicio} a ${fim}`;
    })
    .filter(Boolean)
    .join(" | ");
};

const CadastrarAlunosContent = ({
  initialSolicitacaoId = null,
  initialVagaId = null,
  user = null,
  returnPath = null,
}) => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const initialSelectionConsumed = useRef(false);
  const navigate = useNavigate();

  const fetchSolicitacoes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getSolicitacoesVaga("CONFIRMADA");
      const data = result?.data ? result.data : result;
      const items = Array.isArray(data) ? data : [];
      setSolicitacoes(items);

      if (!initialSelectionConsumed.current && (initialSolicitacaoId || initialVagaId)) {
        const selected = items.find((solicitacao) => {
          if (initialSolicitacaoId) {
            return Number(solicitacao.id) === Number(initialSolicitacaoId);
          }
          return Number(solicitacao.vagaId) === Number(initialVagaId);
        });

        if (selected) {
          setSelectedSolicitacao(selected);
          initialSelectionConsumed.current = true;
        }
      }
    } catch (err) {
      setError(err.message || "Falha ao carregar vagas confirmadas.");
    } finally {
      setLoading(false);
    }
  }, [initialSolicitacaoId, initialVagaId]);

  useEffect(() => {
    fetchSolicitacoes();
  }, [fetchSolicitacoes]);

  const groupedByEstabelecimento = useMemo(() => {
    return solicitacoes.reduce((acc, solicitacao) => {
      const estabelecimentoNome =
        solicitacao.vaga?.estabelecimentoSaude?.nome || "-";
      if (!acc[estabelecimentoNome]) acc[estabelecimentoNome] = [];
      acc[estabelecimentoNome].push(solicitacao);
      return acc;
    }, {});
  }, [solicitacoes]);

  const handleCadastrar = (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
  };

  const handleVoltarDetalhe = () => {
    if (Number(user?.cargoId) === 4) {
      const termoPath = ["/TermoCompromisso", "/TermoCompromissoPendentes"].includes(returnPath)
        ? returnPath
        : "/TermoCompromisso";
      navigate(termoPath);
      return;
    }

    setSelectedSolicitacao(null);
    fetchSolicitacoes();
  };

  if (selectedSolicitacao) {
    return (
      <CadastrarAlunosDetalhe
        solicitacao={selectedSolicitacao}
        onVoltar={handleVoltarDetalhe}
      />
    );
  }

  return (
    <ContentArea>
      {loading && <EmptyMessage>Carregando...</EmptyMessage>}
      {error && <EmptyMessage>{error}</EmptyMessage>}

      {!loading && !error && solicitacoes.length === 0 && (
        <EmptyMessage>Nenhuma vaga confirmada para cadastro de alunos.</EmptyMessage>
      )}

      {!loading && !error && solicitacoes.length > 0 && (
        <ListWrapper>
          {Object.keys(groupedByEstabelecimento).map((estabelecimentoNome) => (
            <UnitTableWrapper key={estabelecimentoNome}>
              <UnitTitle>
                Unidade de saúde:
                <UnitTitleValue>{estabelecimentoNome}</UnitTitleValue>
              </UnitTitle>
              <UnitSeparator />
              <HeaderRow>
                <div>ESPECIALIDADE</div>
                <div>SETOR</div>
                <div>TURNO</div>
                <div>TIPO</div>
                <div>PERÍODO</div>
                <div>
                  <StackedHeaderCell>
                    <span>VAGAS</span>
                    <span>CONCEDIDAS</span>
                  </StackedHeaderCell>
                </div>
                <div>CADASTRAR ALUNOS</div>
              </HeaderRow>

              <UnitRowsWrapper>
                {groupedByEstabelecimento[estabelecimentoNome].map(
                  (solicitacao) => {
                    const total =
                      solicitacao.quantidadeAlunosConcedidos ??
                      solicitacao.quantidadeAlunos ??
                      0;
                    const cadastrados = solicitacao._count?.alunosEstagio ?? 0;
                    const completo = cadastrados >= total && total > 0;

                    return (
                      <ListRow key={solicitacao.id}>
                        <div>
                          {solicitacao.vaga?.unidadeAssistencial?.especialidade
                            ?.nome || "-"}
                        </div>
                        <div>
                          {solicitacao.vaga?.unidadeAssistencial?.nome || "-"}
                        </div>
                        <div>{solicitacao.vaga?.turno || "-"}</div>
                        <div>
                          {formatTipoAtividade(solicitacao.vaga?.tipoAtividade)}
                        </div>
                        <div>{formatPeriodo(solicitacao.periodos)}</div>
                        <div>{total}</div>
                        <ActionsCell>
                          <ProgressRow>
                            <PersonIcon>person</PersonIcon>
                            <span>
                              {cadastrados}/{total}
                            </span>
                          </ProgressRow>
                          {!completo && (
                            <button
                              type="button"
                              className="btn-approve"
                              onClick={() => handleCadastrar(solicitacao)}
                            >
                              Cadastrar
                            </button>
                          )}
                          {completo && (
                            <button
                              type="button"
                              className="btn-approve"
                              onClick={() => handleCadastrar(solicitacao)}
                            >
                              Ver alunos
                            </button>
                          )}
                        </ActionsCell>
                      </ListRow>
                    );
                  }
                )}
              </UnitRowsWrapper>
            </UnitTableWrapper>
          ))}
        </ListWrapper>
      )}
    </ContentArea>
  );
};

export default CadastrarAlunosContent;
